import { useEffect, useState } from 'react'

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/jpg']

export enum AttachmentType {
  File = 'file',
  Image = 'image',
  Pdf = 'pdf',
  Video = 'video',
}

interface AttachmentMetadataIProps {
  mimeType: string | null
  type: AttachmentType
}

const getAttachmentType = (mimeType: string | null): AttachmentType => {
  if (mimeType && IMAGE_MIME_TYPES.includes(mimeType)) {
    return AttachmentType.Image
  }

  if (mimeType === 'application/pdf') {
    return AttachmentType.Pdf
  }

  if (mimeType?.startsWith('video/')) {
    return AttachmentType.Video
  }

  return AttachmentType.File
}

/**
 * Presigned URLs are re-signed on every poll, so the query string changes every
 * few seconds while the asset does not. The S3 path (`userId/assetId`) is the
 * stable identity.
 */
export const getAttachmentAssetKey = (url: string) => {
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}

// Shared across the drawer so the carousel and the PDF report classify once.
const attachmentTypeCache = new Map<string, AttachmentMetadataIProps>()

const classifyUrl = async (url: string): Promise<AttachmentMetadataIProps> => {
  const key = getAttachmentAssetKey(url)
  const cachedMetadata = attachmentTypeCache.get(key)
  if (cachedMetadata) {
    return cachedMetadata
  }

  try {
    // Incident attachments carry no metadata (ENG-30297) — the ranged request
    // returns a single byte and we read the CORS-safelisted Content-Type.
    const response = await fetch(url, { headers: { Range: 'bytes=0-0' } })
    if (!response.ok) {
      return { mimeType: null, type: AttachmentType.File }
    }

    const mimeType =
      response.headers
        .get('Content-Type')
        ?.split(';')[0]
        ?.trim()
        .toLowerCase() ?? null
    const metadata = {
      mimeType,
      type: getAttachmentType(mimeType),
    }

    attachmentTypeCache.set(key, metadata)
    return metadata
  } catch {
    // An unreachable asset is treated as non-renderable so the caller degrades
    // to a download affordance rather than failing outright. Not cached — a
    // network blip should not pin the wrong answer for the session.
    return { mimeType: null, type: AttachmentType.File }
  }
}

export const classifyAttachments = async (urls: string[]) => {
  const safeUrls = urls ?? []
  const classifications = await Promise.all(safeUrls.map(classifyUrl))

  return {
    classifications,
    imageUrls: safeUrls.filter(
      (_, index) => classifications[index].type === AttachmentType.Image
    ),
    otherFileCount: classifications.filter(
      ({ type }) => type !== AttachmentType.Image
    ).length,
  }
}

interface UseAttachmentTypesResultIProps {
  getAttachmentMimeType: (url: string) => string | null
  getAttachmentType: (url: string) => AttachmentType
  isReady: boolean
}

export const useAttachmentTypes = (
  urls: string[]
): UseAttachmentTypesResultIProps => {
  const [attachmentMetadata, setAttachmentMetadata] = useState<Map<
    string,
    AttachmentMetadataIProps
  > | null>(null)
  const cacheKey = (urls ?? []).map(getAttachmentAssetKey).join(',')

  useEffect(() => {
    let isCancelled = false

    classifyAttachments(urls).then(({ classifications }) => {
      if (!isCancelled) {
        setAttachmentMetadata(
          new Map(
            urls.map((url, index) => [
              getAttachmentAssetKey(url),
              classifications[index],
            ])
          )
        )
      }
    })

    return () => {
      isCancelled = true
    }
    // Keyed on the stable asset paths, not the array — the incident query polls
    // every 3s and hands back a new array of freshly signed URLs each time.
  }, [cacheKey])

  return {
    getAttachmentMimeType: (url: string) =>
      attachmentMetadata?.get(getAttachmentAssetKey(url))?.mimeType ?? null,
    getAttachmentType: (url: string) =>
      attachmentMetadata?.get(getAttachmentAssetKey(url))?.type ??
      AttachmentType.File,
    isReady: attachmentMetadata !== null,
  }
}
